import * as THREE from 'three';
import { Enemy } from './enemy';
import { ARCHETYPES, type EnemyKind, type EnemyArchetype } from './archetypes';
import { HABITATS, type Habitat } from './habitats';
import type { BlockPhysics } from '../world/collision';
import type { NavGrid } from '../world/navgrid';
import type { LoadedGLB } from '../core/assets';
import { instantiate } from '../core/assets';
import { SeededRng } from '../core/rng';
import { computeSeparation } from './steering';

const ASSET_RETRY_DELAY = 1; // how soon to re-check after a spawn was skipped because the GLB hadn't loaded yet
const SPAWN_JITTER = 0.6; // meters of random offset inside a habitat slot's own point — kept modest (not a wide scatter) so a narrow habitat (the alley, ai/habitats.ts) can stay provably clear of adjacent ramp/wall footprints; every habitat rectangle is itself sized generously enough that this doesn't read as robotic

export interface EnemyVisualBuild {
  group: THREE.Group;
  materials: THREE.Material[];
  mixer: THREE.AnimationMixer | null;
  clips: Map<string, THREE.AnimationClip> | null;
  baseScale: number;
}

interface HabitatSlot {
  habitat: Habitat;
  kind: EnemyKind;
  homeX: number;
  homeZ: number;
  /** `null` until the slot's first spawn actually builds a visual (may block briefly on an async GLB load) — once built, the SAME `Enemy` instance is reused for every future respawn at this slot (see `Enemy.respawn`, S03's pooling). */
  enemy: Enemy | null;
  respawnCooldown: number;
}

/**
 * Owns the block's population: builds `HabitatSlot`s from `ai/habitats.ts`
 * at construction (a fixed kind per slot, chosen once via seeded RNG — S03
 * scope, replacing S02's flat `SPAWN_POINTS` list), spawns/respawns/pools
 * each slot's `Enemy`, resolves steering separation (ai/steering.ts), and
 * advances every live `Enemy` (feeding it `NavGrid` for ground pathing).
 * Combat resolution (applying damage to the player or to enemies)
 * intentionally lives OUTSIDE this class, in `combat/resolve.ts` — this
 * module only owns population and movement.
 */
export class EnemyManager {
  readonly group = new THREE.Group();
  private readonly enemies: Enemy[] = [];
  private readonly slots: HabitatSlot[] = [];
  private readonly rng = new SeededRng(0x8e17); // deterministic initial population per §0.7's determinism law
  // S10: generalized from the old single `ratGLB` field (a hardcoded
  // special case in `buildVisual()` below) to a real id->GLB registry, now
  // that more than one enemy archetype has a delivered asset. `setAsset`
  // replaces the old `setRatAsset` — main.ts calls it once per manifest id
  // as each GLB resolves, same "tolerate the gap, retry later" async
  // pattern as before (an archetype whose id isn't in this map yet just
  // keeps returning null from `buildVisual`, which `trySpawnSlot` already
  // handles via `ASSET_RETRY_DELAY`).
  private readonly assets = new Map<string, LoadedGLB>();
  // S08 memory audit: an ever-growing scratch pool owned here and reused every
  // tick by computeSeparation() instead of it allocating a fresh Vector3 per
  // enemy — see steering.ts's own comment for the full rationale.
  private readonly separationScratch: THREE.Vector3[] = [];
  // S08: enemy-side reduced-motion flag, threaded into every Enemy.update()
  // call below — see ai/enemy.ts's own comment on why this lives here
  // rather than each Enemy reading a save object directly.
  private flashEnabled = true;

  setFlashEnabled(enabled: boolean): void {
    this.flashEnabled = enabled;
  }

  /**
   * S11: unlike every other toast-triggering event in this project (a real
   * button press, or an aggro/proximity transition a check can arrange
   * deterministically), golden_pigeon's "spotted" announcement fires off a
   * pure sim-time/asset-load timer with no relation to what the player is
   * doing — a GENUINE post-death respawn (150s later, `respawnDelay` in
   * ai/archetypes.ts) can land at an unpredictable point in a long harness
   * run, clobbering an unrelated toast assertion (confirmed real during this
   * session's own verification — not hypothetical). Gates ONLY the announce
   * (`update()`'s `goldenPigeonSpotted` flag), never whether the slot is
   * allowed to spawn/respawn — golden_pigeon is harmless (0 contactDamage, 0
   * aggroRange) regardless of whether it's alive, so letting it freely
   * populate/repopulate the world while disabled costs nothing and keeps
   * spawnInitial()/the respawn loop identical to every other kind's. Checked
   * fresh at the moment of each spawn (ANDed with `wasAlreadyLive`, the
   * respawn loop's own separate history check that keeps the world's very
   * FIRST materialization from ever counting as "spotted" regardless of this
   * flag — see that comment for why the first spawn needed its own fix, a
   * real bug the review caught that this flag alone never could have). This
   * flag's OWN job is narrower now: preventing a later GENUINE respawn from
   * colliding with an unrelated harness assertion. Defaults `true`
   * (identical to a real playthrough); harness/run.mjs calls
   * `setGoldenPigeonEnabled(false)` immediately after page load, before any
   * check runs (and again after every `page.reload()` in that file — a
   * reload re-runs this whole module, resetting the flag to its default).
   */
  private goldenPigeonEnabled = true;

  setGoldenPigeonEnabled(enabled: boolean): void {
    this.goldenPigeonEnabled = enabled;
  }

  constructor() {
    for (const habitat of HABITATS) {
      for (let i = 0; i < habitat.maxPopulation; i++) {
        // Cycle through `kinds` in order rather than an independent RNG
        // pick per slot — guarantees every kind a habitat lists appears at
        // least once whenever population covers the list (every habitat in
        // ai/habitats.ts today has population === kinds.length), instead of
        // leaving it to chance whether e.g. a dive-bomb pigeon ever spawns
        // at all in a given run. A population LARGER than kinds.length
        // still cycles sensibly (round-robins for the extra slots) rather
        // than needing a special case.
        const kind = habitat.kinds[i % habitat.kinds.length];
        const homeX = habitat.area.minX + this.rng.next() * (habitat.area.maxX - habitat.area.minX);
        const homeZ = habitat.area.minZ + this.rng.next() * (habitat.area.maxZ - habitat.area.minZ);
        this.slots.push({ habitat, kind, homeX, homeZ, enemy: null, respawnCooldown: 0 });
      }
    }
  }

  setAsset(id: string, glb: LoadedGLB): void {
    this.assets.set(id, glb);
  }

  getEnemies(): readonly Enemy[] {
    return this.enemies;
  }

  /** S11: NOT gated on `goldenPigeonEnabled` — this runs synchronously from main.ts right after construction, before any caller could ever have toggled that flag away from its `true` default, so the world's initial population always includes golden_pigeon exactly like every other habitat kind. The flag only ever suppresses a LATER respawn (see its own comment and the cooldown loop in `update()` below). */
  spawnInitial(): void {
    this.slots.forEach((_, i) => this.trySpawnSlot(i));
  }

  /** S11: returns whether a live enemy actually entered play this call (false when the asset isn't loaded yet and the slot just re-armed `ASSET_RETRY_DELAY`) — `update()`'s respawn loop uses this to decide whether a genuine golden_pigeon appearance just happened, as opposed to a deferred retry that produced nothing to announce. */
  private trySpawnSlot(index: number): boolean {
    const slot = this.slots[index];
    const jitterX = (this.rng.next() - 0.5) * SPAWN_JITTER;
    const jitterZ = (this.rng.next() - 0.5) * SPAWN_JITTER;
    const spawnX = slot.homeX + jitterX;
    const spawnZ = slot.homeZ + jitterZ;

    if (slot.enemy) {
      // Pooled reuse — same Enemy object, same GLB clone/materials/mixer, just reset in place.
      slot.enemy.respawn(spawnX, spawnZ, slot.habitat.groundHint);
      this.enemies.push(slot.enemy);
      return true;
    }

    const archetype = ARCHETYPES[slot.kind];
    const visual = this.buildVisual(archetype);
    if (!visual) {
      slot.respawnCooldown = ASSET_RETRY_DELAY;
      return false;
    }
    const enemy = new Enemy(archetype, visual, spawnX, spawnZ, index, slot.habitat.groundHint);
    slot.enemy = enemy;
    this.enemies.push(enemy);
    this.group.add(visual.group);
    return true;
  }

  private buildVisual(archetype: EnemyArchetype): EnemyVisualBuild | null {
    if (archetype.assetId) {
      const glb = this.assets.get(archetype.assetId);
      if (!glb) return null; // not loaded yet — trySpawnSlot's ASSET_RETRY_DELAY tries again shortly
      const inst = instantiate(glb, archetype.tint);
      inst.group.scale.setScalar(archetype.scale);
      return { group: inst.group, materials: inst.materials, mixer: inst.mixer, clips: inst.clips, baseScale: archetype.scale };
    }
    return this.buildPrimitiveVisual(archetype);
  }

  /** Gray-box fallback (§0.4) for `rat_king` — kept only for a future archetype that ships with `assetId: null`; every kind that HAD a primitive fallback (trash_lobber/pizza_rat/pigeon/dive_pigeon) now has a real C03 asset per archetypes.ts, so this switch has shrunk to a single named case plus its own default. */
  private buildPrimitiveVisual(archetype: EnemyArchetype): EnemyVisualBuild {
    const group = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({ color: 0x707070, roughness: 0.85 });
    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.3, 0.5, 4, 8), mat);
    body.position.y = 0.5;
    body.rotation.z = Math.PI / 2;
    group.add(body);
    return { group, materials: [mat], mixer: null, clips: null, baseScale: archetype.scale };
  }

  update(
    dt: number,
    physics: BlockPhysics,
    playerPos: THREE.Vector3,
    nav: NavGrid,
  ): {
    removed: Enemy[];
    meleeAttackers: Enemy[];
    projectilesToFire: Array<{ enemy: Enemy; origin: THREE.Vector3; targetPos: THREE.Vector3 }>;
    goldenPigeonSpotted: boolean;
  } {
    const removed: Enemy[] = [];
    const meleeAttackers: Enemy[] = [];
    const projectilesToFire: Array<{ enemy: Enemy; origin: THREE.Vector3; targetPos: THREE.Vector3 }> = [];
    let goldenPigeonSpotted = false;

    const separations = computeSeparation(this.enemies, this.separationScratch);

    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const enemy = this.enemies[i];
      const tickResult = enemy.update(dt, physics, playerPos, separations[i], nav, this.flashEnabled);
      if (tickResult.meleeAttackFired) meleeAttackers.push(enemy);
      if (tickResult.projectileToFire) projectilesToFire.push({ enemy, ...tickResult.projectileToFire });

      if (tickResult.removed) {
        // Pooled reuse (S03): the group stays parented and its materials/
        // geometry stay allocated — only hidden — so the next respawn at
        // this slot reuses the exact same Enemy/visual instead of a fresh
        // GLB clone + a leaked, never-disposed set of materials.
        enemy.group.visible = false;
        this.enemies.splice(i, 1);
        this.slots[enemy.spawnIndex].respawnCooldown = enemy.archetype.respawnDelay;
        removed.push(enemy);
      }
    }

    // Snapshot slot indices before mutating: `trySpawnSlot` can itself
    // re-arm a slot's own cooldown (asset-not-ready-yet retry) — iterating
    // live indices while a slot re-arms mid-pass is safe here since we
    // iterate a fixed-length array by index, but the retry write must not
    // be double-processed the same frame it's set. A plain index sweep
    // (not a live Map re-entry) avoids that ambiguity entirely.
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i];
      if (slot.respawnCooldown <= 0) continue;
      slot.respawnCooldown -= dt;
      if (slot.respawnCooldown <= 0) {
        slot.respawnCooldown = 0;
        // Wave D-1: the `rat_king`-only "never respawn on-screen" frustum
        // gate that used to live here is RETIRED along with `elite_patrol`
        // (ai/habitats.ts) — no slot can produce a `rat_king` kind anymore,
        // so this branch is dead code once that habitat entry is gone, not
        // half-orphaned logic left behind it.
        // Self-caught bug, twice over during the build session's own
        // verification, THEN a third time caught by the formal review (this
        // exact flag has now needed three passes — worth reading in full if
        // this code is ever touched again). Passes 1-2's fixes are preserved
        // below (never gate the SPAWN itself on the flag; gate only the
        // announce). Pass 3, the review's own finding: `spawnInitial()`'s
        // own comment claimed "the world's INITIAL golden_pigeon is never
        // announced either, since spawnInitial() doesn't route through this
        // branch" — true as far as it goes, but IRRELEVANT, because
        // `spawnInitial()` runs synchronously, before ANY `loadGLB(...)`
        // call in main.ts has even been ISSUED (JS guarantees the whole
        // synchronous script body finishes before any `.then()` callback
        // runs), so it ALWAYS fails for every asset-backed archetype
        // (`buildVisual()` finds `this.assets` empty) — including
        // golden_pigeon. That means golden_pigeon's actual first successful
        // spawn ALWAYS happens through THIS branch too (the very first
        // `ASSET_RETRY_DELAY` retry, a couple seconds after boot), which the
        // OLD code below unconditionally flagged as a genuine "it has
        // appeared" — so on literally every real playthrough (the flag
        // defaults `true`), the very FIRST time golden_pigeon's asset
        // finishes loading, the game announced a "rare" sighting for what
        // was actually just the ambient starting population settling in —
        // defeating the entire point of the mechanic for a brand-new
        // player's first, most memorable encounter with it. `wasAlreadyLive`
        // is captured BEFORE calling `trySpawnSlot` specifically to
        // distinguish the two cases correctly: `slot.enemy` is null before a
        // slot's very first successful spawn (whether that spawn happens via
        // `spawnInitial()` for an already-loaded asset, or via this retry
        // loop for one that wasn't) and non-null forever after (S03
        // pooling) — so gating on its value BEFORE the call, not after,
        // correctly fires the announce only on a GENUINE post-death respawn.
        const wasAlreadyLive = slot.enemy !== null;
        const spawned = this.trySpawnSlot(i);
        if (spawned && slot.kind === 'golden_pigeon' && this.goldenPigeonEnabled && wasAlreadyLive) goldenPigeonSpotted = true;
      }
    }

    return { removed, meleeAttackers, projectilesToFire, goldenPigeonSpotted };
  }
}
